/**
 * Internal Complaints Committee — complaint list.
 *
 * ⚠ Confidential. Reaching this screen requires the `icc_complaints`
 * permission, which is granted to the `icc_member` role only.
 *
 * The list deliberately does NOT show the complaint text — the API omits it
 * from the list payload entirely, so a shoulder-surfer or a screenshot of this
 * page reveals nothing about what was reported. Open a row to read it.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Paperclip } from "lucide-react";
import { format } from "date-fns";

import { fetchAll, LOG_MAX_ROWS } from "@/lib/fetchAll";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { TruncationNotice } from "@/components/TruncationNotice";

export const Route = createFileRoute("/_app/icc-complaints/")({ component: ComplaintsPage });

export type ComplaintStatus = "new" | "in_review" | "resolved" | "closed";

export interface ComplaintRow {
  id: number;
  reference: string;
  name: string;
  department: string;
  reg_no: string;
  email: string;
  phone: string | null;
  status: ComplaintStatus;
  /** SQLite has no boolean — the API returns `attachment_r2_key IS NOT NULL`. */
  has_attachment: number;
  submitted_at: number;
  updated_at: number;
}

export const STATUS_META: Record<
  ComplaintStatus,
  { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive" | "outline" }
> = {
  new:       { label: "New",       variant: "warning" },
  in_review: { label: "In review", variant: "default" },
  resolved:  { label: "Resolved",  variant: "success" },
  closed:    { label: "Closed",    variant: "outline" },
};

export function statusBadge(s: ComplaintStatus) {
  const v = STATUS_META[s] ?? STATUS_META.new;
  return <Badge variant={v.variant}>{v.label}</Badge>;
}

function ComplaintsPage() {
  const navigate = useNavigate();

  // Bounded like the other append-only tables: complaints only ever grow, and
  // paginating to the end costs O(n²) rows read in SQLite. See fetchAll.ts.
  const { data } = useQuery({
    queryKey: ["icc-complaints"],
    queryFn: () => fetchAll<ComplaintRow>("/admin/icc-complaints", { maxRows: LOG_MAX_ROWS }),
  });

  const columns: ColumnDef<ComplaintRow, unknown>[] = [
    {
      accessorKey: "reference",
      header: "Reference",
      cell: ({ row }) => (
        <Link
          to="/icc-complaints/$id"
          params={{ id: String(row.original.id) }}
          className="font-mono text-sm font-medium text-primary hover:underline"
        >
          {row.original.reference}
        </Link>
      ),
    },
    {
      accessorKey: "name",
      header: "From",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          <p className="text-xs text-muted-foreground">{row.original.email}</p>
        </div>
      ),
    },
    { accessorKey: "department", header: "Department" },
    { accessorKey: "reg_no", header: "Reg. no / ID" },
    {
      id: "attachment",
      header: "File",
      cell: ({ row }) =>
        row.original.has_attachment ? (
          <Paperclip className="h-4 w-4 text-muted-foreground" aria-label="Has attachment" />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => statusBadge(row.original.status),
    },
    {
      accessorKey: "submitted_at",
      header: "Submitted",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {format(new Date(row.original.submitted_at * 1000), "d MMM yyyy, HH:mm")}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Complaints"
        description="Submitted through the Internal Complaints Form on the public site. Confidential — do not share outside the committee."
      />

      {data?.truncated && <TruncationNotice count={LOG_MAX_ROWS} noun="complaints" />}

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by reference, name, email or department…"
        filters={[{ id: "status", label: "Status", options: (Object.keys(STATUS_META) as ComplaintStatus[])
          .map((s) => ({ value: s, label: STATUS_META[s].label })) }]}
        emptyMessage="No complaints have been submitted."
        onRowClick={(row) => navigate({ to: "/icc-complaints/$id", params: { id: String(row.id) } })}
      />
    </div>
  );
}
